import {
  ActivityPeriod,
  ActivityPeriodSummary,
  ActivityPeriodSummarySource,
  adjustFromTimeZone,
  useGetActivityPeriodSummaryQuery,
  useGetObjectChangeLogsByObjectQuery,
  useGetOperatorActivityPeriodQuery,
  useRefreshActivityPeriodSummaryMutation,
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useMemo, useState} from "react";
import {ActivityPeriodProfile} from "./ActivityPeriodProfile";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../../theme/OtsTheme";

export type ActivityPeriodProfilePageProps = {
  id: string;
  displayName?: string;
  database?: string;
  allowAddComment?: boolean;
  showSummaryActions?: boolean;
  density?: "compact" | "default";
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  onClose?: () => void;
};

const formatActivityPeriodName = (period: ActivityPeriod): string => {
  const from = adjustFromTimeZone(period.fromDttm);
  const to = adjustFromTimeZone(period.toDttm);
  const type = period.type === "DAILY" ? "Daily" : period.type === "MONTHLY" ? "Monthly" : period.type[0] + period.type.slice(1).toLowerCase();
  const category = period.category === "WATER" ? "Water" : period.category === "OIL" ? "Oil" : period.category;
  const range = period.type === "DAILY"
    ? new Intl.DateTimeFormat(undefined, {dateStyle: "short"}).format(from)
    : period.type === "MONTHLY"
      ? new Intl.DateTimeFormat(undefined, {month: "short", year: "numeric"}).format(from)
      : `${new Intl.DateTimeFormat(undefined, {dateStyle: "short"}).format(from)} - ${new Intl.DateTimeFormat(undefined, {dateStyle: "short"}).format(to)}`;
  return `${type} ${category} (${range}) - ${period.operatorName ?? "Unknown operator"}${period.operatorUnitName ? ` (${period.operatorUnitName})` : ""}${period.truckCompanyName ? ` / ${period.truckCompanyName}` : ""}`;
};

/** Data-connected profile page used by the GWT React mount bridge and native React routes. */
export const ActivityPeriodProfilePage = ({
  id,
  displayName,
  database,
  allowAddComment = false,
  showSummaryActions = false,
  density = "compact",
  brand = "water-go",
  colorScheme,
  onClose,
}: ActivityPeriodProfilePageProps) => {
  const [summarySource, setSummarySource] = useState<ActivityPeriodSummarySource>("ROLLUP");
  const periodQuery = useGetOperatorActivityPeriodQuery(id);
  const tenantCode = database ?? periodQuery.data?.database;
  const summaryQuery = useGetActivityPeriodSummaryQuery(showSummaryActions ? {id, source: summarySource, tenantCode} : skipToken);
  const changeLogQuery = useGetObjectChangeLogsByObjectQuery({id, type: "ACTIVITYPERIOD", tenantCode});
  const [refreshSummary, refreshState] = useRefreshActivityPeriodSummaryMutation();

  const loadedSummary = useMemo<ActivityPeriodSummary | undefined>(() => {
    if (summaryQuery.data) return summaryQuery.data;
    const period = periodQuery.data;
    if (!period || showSummaryActions) return undefined;
    return {
      source: "ROLLUP",
      totals: {
        activityPeriodId: period.id,
        ticketCount: period.ticketQty ?? period.totalTicketQty,
        totalQty: period.qty ?? period.totalQty,
        holderAvailable: period.holder,
        adminValidationCount: period.adminValidationCount ?? period.totalAdminValidationCount,
        fieldValidationCount: period.fieldValidationCount ?? period.totalFieldValidationCount,
        businessValidationCount: period.businessValidationCount ?? period.totalBusinessValidationCount,
        disputeCount: period.disputeCount ?? period.totalDisputeCount,
        dirty: false,
        refreshedDttm: period.lastModifiedDttm,
      },
      rateModels: period.cost === undefined ? [] : [{
        activityPeriodId: period.id,
        rateModelId: "activity-period-total",
        rateModelName: "Current total",
        costedTicketQty: period.ticketQty ?? period.totalTicketQty ?? 0,
        totalCost: period.cost,
      }],
      personalResponsibilityCounts: period.personalResponsibilityCounts ?? [],
    };
  }, [periodQuery.data, showSummaryActions, summaryQuery.data]);

  if (periodQuery.isLoading) return <OtsThemeRoot brand={brand} colorScheme={colorScheme} className="p-6 text-sm text-[var(--ots-text-muted)]" role="status">Loading activity period…</OtsThemeRoot>;
  if (!periodQuery.data) return <OtsThemeRoot brand={brand} colorScheme={colorScheme} className="p-6 text-sm text-[var(--ots-error)]" role="alert">The activity period could not be loaded.</OtsThemeRoot>;

  const period = periodQuery.data;
  return <ActivityPeriodProfile
    activityPeriod={period}
    displayName={displayName || formatActivityPeriodName(period)}
    summary={loadedSummary}
    summaryLoading={summaryQuery.isLoading || summaryQuery.isFetching}
    summaryError={summaryQuery.error}
    summarySource={summarySource}
    changeLogs={changeLogQuery.data}
    changeLogsLoading={changeLogQuery.isLoading || changeLogQuery.isFetching}
    tenantCode={tenantCode}
    allowAddComment={allowAddComment}
    showSummaryActions={showSummaryActions}
    refreshingSummary={refreshState.isLoading}
    refreshError={refreshState.error}
    density={density}
    brand={brand}
    colorScheme={colorScheme}
    onSummarySourceChange={setSummarySource}
    onRefreshSummary={async () => {
      await refreshSummary({id, tenantCode}).unwrap();
      setSummarySource("ROLLUP");
    }}
    onClose={onClose}/>;
};
